export type PrepFieldShape = "SIMPLE" | "IN_ARRAY" | "OBJECT";

export type FieldResolution = {
  shape: PrepFieldShape;
  objectValues: boolean;
  joinKeys: string[];
};

function normalizeKey(value: unknown): string | null {
  if (value == null) return null;
  return String(value).trim();
}

function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortKeysDeep);
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortKeysDeep(record[key]);
        return acc;
      }, {});
  }
  return value;
}

function canonicalObjectKey(value: Record<string, unknown>): string {
  return JSON.stringify(sortKeysDeep(value));
}

function valueToJoinKey(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    return canonicalObjectKey(value as Record<string, unknown>);
  }
  return normalizeKey(value);
}

type WalkResult = {
  values: unknown[];
  traversedArray: boolean;
};

function walk(current: unknown, parts: string[], index: number, traversedArray: boolean): WalkResult {
  const result: WalkResult = { values: [], traversedArray };
  if (current == null) {
    return result;
  }
  if (index >= parts.length) {
    result.values.push(current);
    return result;
  }
  if (Array.isArray(current)) {
    result.traversedArray = true;
    for (const item of current) {
      const child = walk(item, parts, index, true);
      result.values.push(...child.values);
    }
    return result;
  }
  if (typeof current === "object") {
    const next = (current as Record<string, unknown>)[parts[index]];
    const child = walk(next, parts, index + 1, traversedArray);
    result.values.push(...child.values);
    result.traversedArray = result.traversedArray || child.traversedArray;
    return result;
  }
  return result;
}

export function resolveField(row: Record<string, unknown> | undefined, fieldPath: string): FieldResolution {
  if (!row || !fieldPath.trim()) {
    return { shape: "SIMPLE", objectValues: false, joinKeys: [] };
  }
  const walkResult = walk(row, fieldPath.split("."), 0, false);
  if (walkResult.values.length === 0) {
    return { shape: "SIMPLE", objectValues: false, joinKeys: [] };
  }
  let objectValues = walkResult.values
    .filter((value) => value != null)
    .every((value) => typeof value === "object" && !Array.isArray(value));
  const shape: PrepFieldShape = walkResult.traversedArray
    ? "IN_ARRAY"
    : walkResult.values.some((value) => typeof value === "object" && value != null && !Array.isArray(value))
      ? "OBJECT"
      : "SIMPLE";
  if (shape === "OBJECT") {
    objectValues = true;
  }
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const value of walkResult.values) {
    const key = valueToJoinKey(value);
    if (key != null && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  return { shape, objectValues, joinKeys: keys };
}

export function resolveFieldFromSamples(
  rows: Record<string, unknown>[] | undefined,
  fieldPath: string
): FieldResolution {
  if (!rows?.length || !fieldPath.trim()) {
    return { shape: "SIMPLE", objectValues: false, joinKeys: [] };
  }
  for (const row of rows) {
    const resolution = resolveField(row, fieldPath);
    if (resolution.joinKeys.length > 0) {
      return resolution;
    }
  }
  return { shape: "SIMPLE", objectValues: false, joinKeys: [] };
}

function isObjectSide(resolution: FieldResolution): boolean {
  return resolution.shape === "OBJECT" || (resolution.shape === "IN_ARRAY" && resolution.objectValues);
}

export function shapesCompatible(upstream: FieldResolution, downstream: FieldResolution): boolean {
  if (isObjectSide(upstream) && isObjectSide(downstream)) {
    return true;
  }
  if (isObjectSide(upstream) || isObjectSide(downstream)) {
    return false;
  }
  const up = upstream.shape;
  const down = downstream.shape;
  return (
    (up === "SIMPLE" && down === "SIMPLE") ||
    (up === "IN_ARRAY" && down === "IN_ARRAY") ||
    (up === "IN_ARRAY" && down === "SIMPLE") ||
    (up === "SIMPLE" && down === "IN_ARRAY")
  );
}

export function edgeFieldMappingCompatible(
  upstreamSamples: Record<string, unknown>[] | undefined,
  upstreamField: string | undefined,
  downstreamSamples: Record<string, unknown>[] | undefined,
  downstreamField: string | undefined
): boolean {
  const upstream = upstreamField?.trim();
  const downstream = downstreamField?.trim();
  if (!upstream || !downstream) {
    return true;
  }
  const upstreamResolution = resolveFieldFromSamples(upstreamSamples, upstream);
  const downstreamResolution = resolveFieldFromSamples(downstreamSamples, downstream);
  if (upstreamResolution.joinKeys.length === 0 || downstreamResolution.joinKeys.length === 0) {
    return true;
  }
  return shapesCompatible(upstreamResolution, downstreamResolution);
}
